import type { NetworkId } from "@akashnetwork/chain-sdk/web";
import type { HttpClientOptions } from "@akashnetwork/http-sdk";
import {
  ApiKeyHttpService,
  AuthHttpService,
  createHttpClient,
  DeploymentSettingHttpService,
  isHttpError,
  ManagedDeploymentHttpService,
  StripeService as HttpStripeService,
  TemplateHttpService,
  TxHttpService,
  UsageHttpService,
  WalletSettingsHttpService
} from "@akashnetwork/http-sdk";
import { LoggerService } from "@akashnetwork/logging";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import type { Axios, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { browserEnvConfig } from "@src/config/browser-env.config";
import { UrlReturnToStack } from "@src/hooks/useReturnTo/UrlReturnToStack";
import { AnalyticsService } from "@src/services/analytics/analytics.service";
import networkStore from "@src/store/networkStore";
import { registry } from "@src/utils/customRegistry";
import { UrlService } from "@src/utils/urlUtils";
import type { ApiUrlService } from "../api-url/api-url.service";
import { withUserToken } from "../auth/auth/interceptors";
import { createContainer } from "../container/createContainer";
import { ErrorHandlerService } from "../error-handler/error-handler.service";
import { ManagedWalletHttpService } from "../managed-wallet-http/managed-wallet-http.service";
import { ProviderProxyService } from "../provider-proxy/provider-proxy.service";
import { StripeService } from "../stripe/stripe.service";
import { UserTracker } from "../user-tracker/user-tracker.service";

export const createAppRootContainer = (config: ServicesConfig) => {
  const apiConfig = { baseURL: config.BASE_API_MAINNET_URL, adapter: "fetch" as const };
  const container = createContainer({
    publicConfig: () => browserEnvConfig,

    applyAxiosInterceptors: (): typeof withInterceptors => {
      const otelInterceptor = (config: InternalAxiosRequestConfig) => {
        if (typeof window !== "undefined" && getRequestOrigin(config) !== window.location.origin) {
          // skip OTEL headers for cross-origin requests in browser because it may fail due to CORS policy
          return config;
        }
        const traceData = container.errorHandler.getTraceData();
        if (traceData.traceIdW3C) config.headers.set("traceparent", traceData.traceIdW3C);
        if (traceData?.baggage) config.headers.set("Baggage", traceData.baggage);
        return config;
      };
      return (axiosInstance, interceptors?) =>
        withInterceptors(axiosInstance, {
          request: [config.globalRequestMiddleware, otelInterceptor, ...(interceptors?.request || [])],
          response: [...(interceptors?.response || [])]
        });
    },
    stripe: () =>
      container.applyAxiosInterceptors(new HttpStripeService(apiConfig), {
        request: [withUserToken]
      }),
    stripeService: () =>
      new StripeService({
        config: {
          publishableKey: container.publicConfig.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
        }
      }),
    tx: () =>
      container.applyAxiosInterceptors(new TxHttpService(registry, apiConfig), {
        request: [withUserToken]
      }),
    template: () => {
      const httpClient = container.createAxios({
        baseURL: container.publicConfig.NEXT_PUBLIC_BASE_TEMPLATES_URL
      });
      if (config.runtimeEnv === "browser") {
        // Need to remove these extra headers to avoid CORS preflight requests
        delete httpClient.defaults.headers["Content-Type"];
        delete httpClient.defaults.headers.common.Accept;
      }
      return new TemplateHttpService(httpClient);
    },
    usage: () =>
      container.applyAxiosInterceptors(new UsageHttpService(apiConfig), {
        request: [withUserToken]
      }),
    auth: () =>
      container.applyAxiosInterceptors(new AuthHttpService(apiConfig), {
        request: [withUserToken]
      }),
    networkStore: () => networkStore,
    providerProxy: () => {
      const getBaseUrl = () => config.BASE_PROVIDER_PROXY_URL.replace("%{NETWORK}", container.networkStore.selectedNetworkId);
      return new ProviderProxyService(
        container.applyAxiosInterceptors(container.createAxios({ baseURL: "/" }), {
          request: [config => ({ ...config, baseURL: getBaseUrl() })]
        }),
        container.logger,
        () => new WebSocket(getBaseUrl().replace(/^http/, "ws"))
      );
    },
    deploymentSetting: () =>
      container.applyAxiosInterceptors(new DeploymentSettingHttpService(apiConfig), {
        request: [withUserToken]
      }),
    managedDeployment: () => {
      const httpClient = container.applyAxiosInterceptors(createHttpClient(apiConfig), {
        request: [withUserToken]
      });
      return new ManagedDeploymentHttpService(httpClient);
    },
    walletSettings: () =>
      container.applyAxiosInterceptors(new WalletSettingsHttpService(apiConfig), {
        request: [withUserToken]
      }),
    apiKey: () =>
      container.applyAxiosInterceptors(new ApiKeyHttpService(apiConfig), {
        request: [withUserToken]
      }),
    externalApiHttpClient: () => {
      const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json"
      };
      if (config.runtimeEnv === "nodejs") {
        headers["User-Agent"] = "JunoaNetworkConsole/1.0 (https://net.junoa.ai)";
      }

      return container.createAxios({
        headers
      });
    },
    createAxios:
      () =>
      (options?: HttpClientOptions): AxiosInstance =>
        createHttpClient(options),
    analyticsService: () =>
      new AnalyticsService({
        amplitude: {
          enabled: container.publicConfig.NEXT_PUBLIC_AMPLITUDE_ENABLED,
          apiKey: container.publicConfig.NEXT_PUBLIC_AMPLITUDE_API_KEY,
          serverUrl: container.publicConfig.NEXT_PUBLIC_AMPLITUDE_PROXY_URL
        },
        ga: {
          measurementId: container.publicConfig.NEXT_PUBLIC_GA_MEASUREMENT_ID,
          enabled: container.publicConfig.NEXT_PUBLIC_GA_ENABLED
        }
      }),
    apiUrlService: config.apiUrlService,
    managedWalletService: () => {
      const httpClient = container.applyAxiosInterceptors(createHttpClient(apiConfig), {
        request: [withUserToken],
        response: [
          response => {
            if (response.config.url === "v1/start-trial" && response.config.method === "post" && response.status === 200) {
              container.analyticsService.track("trial_started", { category: "billing", label: "Trial Started" });
            }
            return response;
          }
        ]
      });
      return new ManagedWalletHttpService(httpClient, container.analyticsService);
    },
    queryClient: () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry(failureCount, error) {
              return isHttpError(error) && !!error.response && error.response.status >= 500 && failureCount < 3;
            }
          }
        },
        queryCache: new QueryCache({
          onError: error => container.errorHandler.reportError({ error })
        }),
        mutationCache: new MutationCache({
          onError: error => container.errorHandler.reportError({ error })
        })
      }),
    errorHandler: () => new ErrorHandlerService(container.logger),
    logger: () =>
      new LoggerService({
        name: `app-${config.runtimeEnv}`,
        browser: {
          disabled: config.runtimeEnv !== "browser",
          // enable serialization of log events, so then we can see more details about errors in Sentry
          serialize: true,
          asObject: true
        }
      }),
    urlService: () => UrlService,
    urlReturnToStack: () => UrlReturnToStack,
    userTracker: () => new UserTracker()
  });

  return container;
};

export interface ServicesConfig {
  BASE_API_MAINNET_URL: string;
  BASE_PROVIDER_PROXY_URL: string;
  MANAGED_WALLET_NETWORK_ID: NetworkId;
  globalRequestMiddleware?: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
  runtimeEnv: "nodejs" | "browser";
  apiUrlService: () => ApiUrlService;
}

export function withInterceptors<T extends Axios | AxiosInstance = AxiosInstance>(axios: T, interceptors?: Interceptors) {
  interceptors?.request?.forEach(interceptor => axios.interceptors.request.use(interceptor));
  interceptors?.response?.forEach(interceptor => axios.interceptors.response.use(interceptor));
  return axios;
}

type Interceptor<T> = (value: T) => T | Promise<T>;
interface Interceptors {
  request?: Array<Interceptor<InternalAxiosRequestConfig> | undefined>;
  response?: Array<Interceptor<AxiosResponse> | undefined>;
}

function getRequestOrigin(config: InternalAxiosRequestConfig) {
  if (config.url?.startsWith("http")) return new URL(config.url).origin;

  let baseUrl = config.baseURL ?? "";
  if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
  let url = config.url ?? "";
  if (!url.startsWith("/")) url = `/${url}`;
  const fullUrl = baseUrl + url;
  return fullUrl.startsWith("http") ? new URL(fullUrl).origin : window.location.origin;
}
